import React, { useEffect, useState } from "react";

import { equals } from "lodash/fp";
import { matchPath, useNavigate, useResolvedPath } from "react-router-dom";

import {
  Box,
  Tabs as MuiTabs,
  Tab as MuiTab,
  TabProps as MuiTabProps,
  TabsProps as MuiTabsProps,
  Typography,
  styled,
  SxProps,
  SvgIconProps,
} from "@mui/material";

import Stack from "@rui/Stack";

export interface Tab {
  id: string;
  label: string | React.ReactNode;
  icon?: React.FunctionComponent<SvgIconProps>;
  defaultPath?: string;
  beta?: boolean;
  path: string;
  testId?: string;
  disabled?: boolean;
  notificationCount?: number;
}

interface TabsProps {
  tabs: Tab[];
  role?: string;
  defaultRoute?: string;
  sx?: SxProps;
  withSearchParams?: boolean;
  onChange?: (path: string) => void;
  variant?: MuiTabsProps["variant"];
  actions?: React.ReactElement;
  centered?: boolean;
  size?: "sm" | "lg";
}

const StyledTabs = styled(MuiTabs)(({ theme }) => ({
  position: "relative",
  height: "55px",
  "& .MuiTabs-indicator": {
    height: "3px",
    borderRadius: "2px",
  },
  zIndex: 2,
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    height: "40px",
    minHeight: "40px",

    "& .MuiTabs-flexContainer": {
      gap: theme.spaces.small,
    },
  },
}));

const NotificationBadge = styled(Stack)(({ theme }) => ({
  borderRadius: "6px",
  backgroundColor: theme.palette.shades.primary[500],
  color: "#fff",
  minWidth: "16px",
  minHeight: "18px",
  paddingRight: "5px",
  paddingLeft: "5px",
}));

interface StyledMuiTabProps extends MuiTabProps {
  size: "sm" | "lg";
}

const StyledTab = styled(MuiTab, {
  shouldForwardProp: (prop) => prop !== "size",
})<StyledMuiTabProps>(({ theme, size }) => ({
  borderRadius: "8px",
  "&.Mui-selected": {
    backgroundColor: "#F1F4FF",
  },
  [theme.breakpoints.up("md")]: {
    minWidth: size == "sm" ? "100px" : "160px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "36px",
    minHeight: "36px",
    padding: theme.spaces.small,
    borderRadius: "6px",
  },
}));
const TabLabel = ({
  label,
  icon: Icon,
  beta = false,
  notificationCount = 0,
}: {
  label: string | React.ReactNode;
  icon?: React.FunctionComponent<SvgIconProps>;
  beta?: boolean;
  notificationCount?: number;
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={{ width: "100%" }}
      spacing={1}
    >
      {Icon && <Icon sx={{ width: "16px" }} />}
      <Typography
        variant="bodyPrimaryMed"
        sx={{
          fontWeight: 400,
          display: { xxs: Icon ? "none" : "block", md: "block" },
        }}
      >
        {label}
      </Typography>
      {beta && (
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{
            height: "16px",
            width: "32px",
            color: "#fff",

            background:
              "linear-gradient(48.39deg, #4361EE 23.98%, #7A61FF 80.34%)",
            borderRadius: "2px",
            fontSize: "10px",
          }}
        >
          beta
        </Stack>
      )}
      {notificationCount > 0 && (
        <NotificationBadge variant="center">
          <Typography variant="labelMed">{notificationCount}</Typography>
        </NotificationBadge>
      )}
    </Stack>
  );
};

const Tabs = ({
  tabs,
  defaultRoute,
  withSearchParams = false,
  onChange,
  variant,
  actions,
  centered,
  size = "lg",
  sx,
}: TabsProps) => {
  const navigate = useNavigate();

  const indexOfMatched = tabs
    .map((tab) => useResolvedPath(tab.path))
    .findIndex((path) =>
      withSearchParams
        ? matchPath(path.pathname, location.pathname) &&
          equals(path.search, location.search)
        : matchPath(path.pathname, location.pathname)
    );

  const [currentTab, setCurrentTab] = useState(indexOfMatched);

  const numEnabledTabs = tabs.filter((t) => !t.disabled).length;
  useEffect(() => {
    if (
      (indexOfMatched < 0 || indexOfMatched >= numEnabledTabs) &&
      defaultRoute
    ) {
      // if the user directly navigates to a disabled or non-existent tab,
      // or was previously on a tab that is now disabled, redirect to default route
      return navigate(defaultRoute, { replace: true });
    }
    setCurrentTab(indexOfMatched);
  }, [indexOfMatched, numEnabledTabs]);

  const handleChangeTab = (path: string) => () => {
    if (onChange) {
      onChange(path);
    } else {
      navigate(path);
    }
  };

  return (
    <Stack sx={{ width: "100%", position: "relative", ...sx }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <StyledTabs
          value={Math.max(0, currentTab)}
          sx={{ width: "100%", px: "20px" }}
          variant={variant}
          centered={centered}
        >
          {tabs.map((tab) => (
            <StyledTab
              data-test-id={tab.testId}
              key={tab.path}
              label={
                <TabLabel
                  label={tab.label}
                  icon={tab.icon}
                  beta={tab.beta}
                  notificationCount={tab.notificationCount}
                />
              }
              onClick={handleChangeTab(tab.defaultPath ?? tab.path)}
              size={size}
              disabled={tab.disabled}
            />
          ))}
        </StyledTabs>
        {actions && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="end"
            sx={{ width: { lg: "40%", sm: "25%" }, pr: "20px" }}
            spacing={1}
          >
            {actions}
          </Stack>
        )}
      </Stack>
      <Box
        sx={{
          height: "3px",
          width: "100%",
          position: "absolute",
          bottom: 0,
          bgcolor: "shades.neutral.50",
          borderRadius: "2px",
          zIndex: 0,
        }}
      />
    </Stack>
  );
};

export default Tabs;
